import { EVENTS, subscribe } from '@utilities/events';
import { getPromotionDiscount, renderPriceMarkup } from '@utilities/theme';

/**
 * ProductPrice
 * Manages price display, sale/final-sale badge toggling for a product variant.
 *
 * Relies on its parent <product-overview> for product/variant state.
 * Subscribes to (via pub-sub):
 *   - product-overview:initialized
 *   - product-overview:variant-change
 *   - product-overview:no-variant
 *   - product-overview:swatch-change   (show the color's price before a variant resolves)
 *   - product-promotion:change         (apply an automatic promotion discount to the price)
 */
if (!customElements.get('product-price')) {
  customElements.define('product-price', class ProductPrice extends HTMLElement {
    constructor() {
      super();

      /** @type {HTMLElement|null} */
      this.productOverview = null;

      /** Last swatch data seen (used to keep price/sale correct while no variant is resolved) */
      this.currentSwatchData = null;

      /** Active promotion, used to re-apply the discount after every price render */
      this.promotion = {};

      /** Last price markup received from the server, used to clear an applied discount */
      this.lastPriceHtml = null;

      /** URL of the variant now on screen, used to match a promotion to its variant */
      this.currentVariantUrl = null;

      /** URL the promotion in hand was fetched for */
      this.promotionVariantUrl = null;

      /** Whether a discount is currently painted over the server price markup */
      this.promotionApplied = false;

      // Bound handlers for clean removal
      this.boundHandleInitialized = this.handleInitialized.bind(this);
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.boundHandleNoVariant = this.handleNoVariant.bind(this);
      this.boundHandleSwatchChange = this.handleSwatchChange.bind(this);
      this.boundHandlePromotionChange = this.handlePromotionChange.bind(this);
    }

    // ─── Debug ──────────────────────────────────────────────
    allowDebug() {
      return this.productOverview?.classList.contains('debug') ?? false;
    }

    // ─── Lifecycle ──────────────────────────────────────────
    connectedCallback() {
      this.productOverview = this.closest('product-overview');
      if (!this.productOverview) {
        console.warn('ProductPrice: No <product-overview> parent found.');
        return;
      }

      this.cacheElements();

      // If the parent is already initialised, run immediately
      if (this.productOverview.classList.contains('product-overview--initialized')) {
        this.onReady(this.productOverview.product, this.productOverview.variant);
      }

      this.subscribeToEvents();
    }

    disconnectedCallback() {
      this.unsubscribeFromEvents();
      this.productOverview = null;
    }

    // ─── Element Cache ──────────────────────────────────────
    cacheElements() {
      this.priceTextElements = this.querySelectorAll('.product__price-text');
      this.saleProperties = this.querySelectorAll('.sale-property');
      this.finalSaleProperties = this.querySelectorAll('.final-sale-property');
    }

    // ─── Subscriptions ─────────────────────────────────────
    subscribeToEvents() {
      this.unsubscribers = [
        subscribe(EVENTS.PRODUCT_OVERVIEW_INITIALIZED, this.boundHandleInitialized),
        subscribe(EVENTS.PRODUCT_OVERVIEW_VARIANT_CHANGE, this.boundHandleVariantChange),
        subscribe(EVENTS.PRODUCT_OVERVIEW_NO_VARIANT, this.boundHandleNoVariant),
        subscribe(EVENTS.PRODUCT_OVERVIEW_SWATCH_CHANGE, this.boundHandleSwatchChange),
        subscribe(EVENTS.PRODUCT_PROMOTION_CHANGE, this.boundHandlePromotionChange),
      ];
    }

    unsubscribeFromEvents() {
      if (this.unsubscribers) {
        this.unsubscribers.forEach((unsub) => unsub());
        this.unsubscribers = [];
      }
    }

    // ─── Event Handlers ─────────────────────────────────────
    handleInitialized(data) {
      if (data.productOverview !== this.productOverview) return;
      this.onReady(data.product, data.variant);
    }

    handleVariantChange(data) {
      if (data.productOverview !== this.productOverview) return;
      const { variant } = data;
      if (!variant || Object.keys(variant).length === 0) return;

      this.currentVariantUrl = variant.url || null;
      this.setPrice(variant.price_html);
      this.updateSale(this.resolveIsSale(variant));
      this.updateFinalSale(this.resolveIsFinalSale(variant));
    }

    /**
     * A product is "on sale" for the SALE flag if either the resolved variant is
     * discounted (compare-at > price) OR the selected color is flagged as a sale color
     * (Greyson sale_colors metafield, surfaced as swatch.isSale). The two systems are
     * independent, so a sale color without a compare-at price still shows SALE.
     * final sale overrides sale
     */
    resolveIsSale(variant) {
      if (variant?.isFinalSale === true) return false;
      if (this.getActiveSwatchData()?.isFinalSale === true) return false;
      if (variant?.isSale === true) return true;
      return this.getActiveSwatchData()?.isSale === true;
    }

    resolveIsFinalSale(variant) {
      if (variant?.isFinalSale === true) return true;
      return this.getActiveSwatchData()?.isFinalSale === true;
    }

    handleNoVariant(data) {
      if (data.productOverview !== this.productOverview) return;
      // No resolved variant: fall back to the selected color's pricing so the price
      // and SALE flag stay correct (e.g. while only the color is chosen).
      this.applySwatchPricing();
    }

    /**
     * When the color swatch changes, reflect that color's price immediately using the
     * swatch's pre-rendered price HTML — so sale colors (and any color with a different
     * price) show the correct price and SALE flag before a full variant is resolved.
     * Once a variant resolves, handleVariantChange runs setPrice again; setPrice is a
     * no-op when the HTML is identical, so there's no redundant re-render.
     */
    handleSwatchChange(data) {
      if (data.productOverview !== this.productOverview) return;
      if (!data.data) return;
      this.currentSwatchData = data.data;
      this.applySwatchPricing();
    }

    /**
     * Store the resolved promotion and re-apply it to the price already on screen.
     *
     * The promotion is fetched after first paint, so the price is normally rendered
     * before this arrives. Applying it here updates that rendered price in place.
     *
     * Scoped to the shared <product-overview>, because the promotion element sits in
     * the sibling `_pdp_promotion` block, not inside <product-price>.
     */
    handlePromotionChange(data) {
      // The PDP renders <product-promotion> in its own block, a sibling of this one,
      // so scope on the shared <product-overview> ancestor rather than containment.
      if (!this.productOverview) return;
      if (!this.productOverview.contains(data.triggeringElement)) return;
      this.promotion = data.promotion || {};
      // The promotion element fetches per variant URL, so record which variant this
      // payload belongs to. applyPromotion uses it to avoid discounting a variant the
      // promotion was not resolved against.
      this.promotionVariantUrl =
        data.triggeringElement?.config?.fetch?.url || this.currentVariantUrl || null;
      // An empty promotion means the discount no longer applies (e.g. the shopper
      // moved to a variant the promotion excludes). Restore the undiscounted server
      // price rather than leaving a stale cross-out on screen.
      if (!getPromotionDiscount(this.promotion)) {
        this.restoreServerPrice();
        return;
      }
      this.applyPromotion();
    }

    /**
     * Re-renders the last server-provided price markup, dropping any applied discount.
     *
     * Used when a promotion stops applying. The server HTML is the source of truth for
     * the variant's real price, so re-rendering it is what clears the discount.
     */
    restoreServerPrice() {
      if (!this.lastPriceHtml) return;
      if (!this.priceTextElements?.length) return;
      for (const el of this.priceTextElements) {
        const temp = document.createElement('div');
        temp.innerHTML = this.lastPriceHtml;
        el.replaceChildren(...temp.childNodes);
      }
    }

    /**
     * Apply the active promotion discount to the rendered price.
     *
     * Runs after every price render, because setPrice replaces the price markup with
     * fresh server HTML that has no discount applied to it.
     */
    applyPromotion() {
      this.promotionApplied = false;
      if (!this.priceTextElements?.length) return;
      // Only discount the variant the promotion was actually fetched for. A promotion
      // resolves per variant, so applying one to a different variant could show a
      // price the cart will not honor. When the two are out of step, resync from the
      // promotion element, which holds the payload for the variant it last fetched.
      if (!getPromotionDiscount(this.promotion) || !this.isPromotionForCurrentVariant()) {
        if (!this.syncPromotionFromElement()) return;
        if (!this.isPromotionForCurrentVariant()) return;
      }
      for (const el of this.priceTextElements) {
        el.replaceChildren(renderPriceMarkup(this.lastPriceHtml, this.promotion));
      }
      // Read the result from the DOM rather than the helper's return value. The helper
      // reports false for a price it has already discounted, so trusting the return
      // would clear this flag while a discount is still on screen.
      this.promotionApplied = this.hasDiscountedPriceRendered();
    }

    /**
     * Whether a discount is currently rendered in the price markup.
     *
     * @returns {boolean}
     */
    hasDiscountedPriceRendered() {
      if (!this.priceTextElements?.length) return false;
      for (const el of this.priceTextElements) {
        if (el.querySelector('.price-item[data-promotion-applied="true"]')) return true;
      }
      return false;
    }

    /**
     * Whether the promotion in hand was fetched for the variant now on screen.
     *
     * The promotion element refetches on variant change, so a promotion and a variant
     * can be briefly out of step. Comparing the two URLs keeps a promotion from
     * discounting a variant it was not resolved against.
     *
     * Returns true when either URL is unknown, so a product-level promotion still
     * applies on a page that never resolves a variant URL.
     */
    isPromotionForCurrentVariant() {
      if (!this.promotionVariantUrl || !this.currentVariantUrl) return true;
      return this.promotionVariantUrl === this.currentVariantUrl;
    }

    /**
     * Refreshes the promotion from the promotion element's own state.
     *
     * The promotion element skips publishing a change when a refetch returns the same
     * payload, so a variant change that keeps the same promotion never fires an event.
     * Reading the element directly keeps the price in step with the variant in that
     * case. Returns true when a promotion for the current variant is available.
     */
    syncPromotionFromElement() {
      const promotionElement =
        this.promotionElement ||
        this.productOverview?.querySelector('product-promotion');
      if (!promotionElement) return false;
      this.promotionElement = promotionElement;

      const fetchUrl = promotionElement.config?.fetch?.url || null;
      // Only trust the element once its fetch has caught up with the current variant.
      if (fetchUrl && this.currentVariantUrl && fetchUrl !== this.currentVariantUrl) {
        return false;
      }

      const promotion = promotionElement.getPromotion?.() || {};
      this.promotion = promotion;
      this.promotionVariantUrl = fetchUrl || this.currentVariantUrl;
      return !!getPromotionDiscount(promotion);
    }

    /**
     * Resolve the currently selected swatch (the one we last received via swatch-change,
     * or the initially selected swatch from the product data on first render).
     */
    getActiveSwatchData() {
      if (this.currentSwatchData && Object.keys(this.currentSwatchData).length > 0) {
        return this.currentSwatchData;
      }
      const swatches = this.productOverview?.product?.swatches;
      if (Array.isArray(swatches) && swatches.length > 0) {
        return swatches.find((swatch) => swatch.selected) || null;
      }
      return null;
    }

    /**
     * Apply price + SALE flag from the active swatch. Final sale is variant-specific and
     * unknown at the swatch level, so it's cleared until a variant resolves.
     */
    applySwatchPricing() {
      const swatchData = this.getActiveSwatchData();
      if (swatchData?.price_html) {
        this.setPrice(swatchData.price_html);
      }
      this.updateSale(swatchData?.isSale === true);
      this.updateFinalSale(swatchData?.isFinalSale === true);
    }

    // ─── Initial render when parent is ready ────────────────
    onReady(product, variant) {
      if (!variant || Object.keys(variant).length === 0) {
        this.applySwatchPricing();
        return;
      }
      this.currentVariantUrl = variant.url || null;
      this.setPrice(variant.price_html);
      this.updateSale(this.resolveIsSale(variant));
      this.updateFinalSale(this.resolveIsFinalSale(variant));
    }

    // ─── Price Display ──────────────────────────────────────
    setPrice(priceHtml) {
      if (!priceHtml) return;
      if (!this.priceTextElements || this.priceTextElements.length === 0) return;
      if (this.allowDebug()) {
        console.log('ProductPrice: setPrice', priceHtml);
      }
      // Compare against the last HTML this component was given, not against the live
      // DOM: applying a promotion rewrites that DOM, so an innerHTML comparison would
      // no longer match and would skip a render that has to happen.
      const isNewPrice = this.lastPriceHtml !== priceHtml;
      if (!isNewPrice && !this.promotionApplied) return;

      this.lastPriceHtml = priceHtml;

      // Resolve the promotion before rendering, so the discount is baked into the
      // markup off-DOM. Writing the plain price first and discounting it afterwards
      // paints two different prices and reads as a flash.
      const promotion = this.resolveApplicablePromotion();

      for (const el of this.priceTextElements) {
        el.replaceChildren(renderPriceMarkup(priceHtml, promotion));
      }

      this.promotionApplied = this.hasDiscountedPriceRendered();
    }

    /**
     * Returns the promotion that applies to the variant on screen, or null.
     *
     * A promotion is only valid for the variant it was fetched for. When the two are
     * out of step, resync from the promotion element, which holds the payload for the
     * variant it last fetched.
     *
     * @returns {Object|null} The applicable promotion, or null when none applies
     */
    resolveApplicablePromotion() {
      if (!getPromotionDiscount(this.promotion) || !this.isPromotionForCurrentVariant()) {
        if (!this.syncPromotionFromElement()) return null;
        if (!this.isPromotionForCurrentVariant()) return null;
      }
      return this.promotion;
    }

    // ─── Sale / Final-Sale Toggle ───────────────────────────
    updateSale(show) {
      if (show) {
        this.saleProperties.forEach((prop) => prop.removeAttribute('disabled'));
        this.finalSaleProperties.forEach((prop) => prop.setAttribute('disabled', true));
      } else {
        this.saleProperties.forEach((prop) => prop.setAttribute('disabled', true));
      }
    }

    updateFinalSale(show) {
      if (show) {
        this.finalSaleProperties.forEach((prop) => prop.removeAttribute('disabled'));
        this.saleProperties.forEach((prop) => prop.setAttribute('disabled', true));
      } else {
        this.finalSaleProperties.forEach((prop) => prop.setAttribute('disabled', true));
      }
    }

    // ─── Public Getters (backward-compat helpers) ───────────
    getPriceRangeHTML() {
      return this.productOverview?.product?.price_range_html ?? null;
    }
  });
}
